import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "./lib/AuthContext";
import { useAuth } from "./lib/useAuth";
import { LanguageProvider } from "./lib/language";
import { ThemeProvider } from "./lib/ThemeContext";
import { AppShell } from "./components/layout";

import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Register from "./pages/Register";
import NotFound from "./pages/not-found";
import Strutture from "./pages/Strutture";
import StruttureTree from "./pages/StruttureTree";
import StrutturaFormPage from "./pages/StrutturaFormPage";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import Camere from "./pages/Camere";
import Prenotazioni from "./pages/Prenotazioni";
import PrenotazionePage from "./pages/PrenotazionePage";
import Ospiti from "./pages/Ospiti";
import OspitePage from "./pages/OspitePage";
import Calendario from "./pages/Calendario";
import Collaboratori from "./pages/Collaboratori";
import Finanze from "./pages/Finanze";
import FinanzeReport from "./pages/FinanzeReport";
import Impostazioni from "./pages/Impostazioni";
import StrutturePage from "./pages/StrutturePage";
import CameraPage from "./pages/CameraPage";
import Host from "./pages/Host";
import HostPage from "./pages/HostPage";
import AttivitaPage from "./pages/AttivitaPage";
import AttivitaDettaglioPage from "./pages/AttivitaDettaglioPage";
import AttivitaPulizie from "./pages/AttivitaPulizie";
import Manutenzione from "./pages/Manutenzione";
import ManutenzioneTicketPage from "./pages/ManutenzioneTicketPage";
import CollaboratorePage from "./pages/CollaboratorePage";
import Contratti from "./pages/Contratti";
import ContrattoPage from "./pages/ContrattoPage";
import ContrattoTemplatePage from "./pages/ContrattoTemplatePage";
import Segnalazioni from "./pages/Segnalazioni";
import SegnalazionePage from "./pages/SegnalazionePage";
import Recensioni from "./pages/Recensioni";
import RecensionePage from "./pages/RecensionePage";
import FinanzePanoramica from "./pages/FinanzePanoramica";
import FinanzeEntrate from "./pages/FinanzeEntrate";
import FinanzeUscite from "./pages/FinanzeUscite";
import Pricing from "./pages/Pricing";
import ICalSync from "./pages/ICalSync";
import NuovaPrenotazione from "./pages/NuovaPrenotazione";
import DatabaseOspiti from "./pages/DatabaseOspiti";
import GuestAppConfig from "./pages/GuestAppConfig";
import GuestAppAnteprima from "./pages/GuestAppAnteprima";
import AdminOnboarding from "./pages/AdminOnboarding";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        if (error?.status === 404 || error?.status === 401) return false;
        return failureCount < 1;
      },
      staleTime: 30000,
      throwOnError: false,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { currentUser, isLoadingAuth } = useAuth();

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <div className="text-muted-foreground text-sm">Caricamento...</div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AuthenticatedApp() {
  return (
    <Routes>
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route path="/" element={<Dashboard />} />
        {/* Admin Routes */}
        <Route path="/super-admin" element={<SuperAdminDashboard />} />
        {/* Strutture */}
        <Route path="/strutture" element={<Strutture />} />
        <Route path="/strutture/lista" element={<Strutture />} />
        <Route path="/strutture/tree" element={<StruttureTree />} />
        <Route path="/strutture/camere" element={<Camere />} />
        <Route path="/strutture/camere/:id" element={<CameraPage />} />
        <Route path="/strutture/nuovo" element={<StrutturaFormPage />} />
        <Route path="/strutture/modifica/:id" element={<StrutturaFormPage />} />

        <Route path="/strutture/:id" element={<StrutturePage />} />
        {/* Prenotazioni */}
        <Route path="/prenotazioni" element={<Prenotazioni />} />
        <Route path="/prenotazioni/lista" element={<Prenotazioni />} />
        <Route path="/prenotazioni/nuovo" element={<NuovaPrenotazione />} />
        <Route path="/prenotazioni/lista/nuovo" element={<PrenotazionePage />} />
        <Route path="/prenotazioni/lista/:id" element={<PrenotazionePage />} />
        <Route path="/prenotazioni/calendario" element={<Calendario />} />
        <Route path="/prenotazioni/ical" element={<ICalSync />} />
        <Route path="/prenotazioni/ospiti" element={<Ospiti />} />
        <Route path="/prenotazioni/ospiti/nuovo" element={<OspitePage />} />
        <Route path="/prenotazioni/ospiti/:id" element={<OspitePage />} />
        {/* Ospiti */}
        <Route path="/ospiti/database" element={<DatabaseOspiti />} />
        {/* Guest App */}
        <Route path="/guest-app/configurazione" element={<GuestAppConfig />} />
        <Route path="/guest-app/anteprima" element={<GuestAppAnteprima />} />
        {/* Team */}
        <Route path="/team" element={<Collaboratori />} />
        <Route path="/team/host" element={<Host />} />
        <Route path="/team/host/nuovo" element={<HostPage />} />
        <Route path="/team/host/:id" element={<HostPage />} />
        <Route path="/team/collaboratori" element={<Collaboratori />} />
        <Route path="/team/collaboratori/nuovo" element={<CollaboratorePage />} />
        <Route path="/team/collaboratori/:id" element={<CollaboratorePage />} />
        <Route path="/team/pulizie" element={<AttivitaPulizie />} />
        <Route path="/team/manutenzione" element={<Manutenzione />} />
        <Route path="/team/manutenzione/nuovo" element={<ManutenzioneTicketPage />} />
        <Route path="/team/manutenzione/:id" element={<ManutenzioneTicketPage />} />
        {/* Finanze */}
        <Route path="/finanze" element={<FinanzePanoramica />} />
        <Route path="/finanze/panoramica" element={<FinanzePanoramica />} />
        <Route path="/finanze/entrate" element={<FinanzeEntrate />} />
        <Route path="/finanze/uscite" element={<FinanzeUscite />} />
        <Route path="/finanze/transazioni" element={<Navigate to="/finanze/uscite" replace />} />
        <Route path="/finanze/report" element={<FinanzeReport />} />
        {/* Operativo */}
        <Route path="/operativo" element={<AttivitaPage />} />
        <Route path="/operativo/attivita" element={<AttivitaPage />} />
        <Route path="/operativo/attivita/:id" element={<AttivitaDettaglioPage />} />
        <Route path="/operativo/segnalazioni" element={<Segnalazioni />} />
        <Route path="/operativo/segnalazioni/nuovo" element={<SegnalazionePage />} />
        <Route path="/operativo/segnalazioni/:id" element={<SegnalazionePage />} />
        <Route path="/operativo/recensioni" element={<Recensioni />} />
        <Route path="/operativo/recensioni/:id" element={<RecensionePage />} />
        <Route path="/operativo/attivita/nuovo" element={<AttivitaDettaglioPage />} />
        <Route path="/operativo/attivita/:id" element={<AttivitaDettaglioPage />} />
        {/* Altro */}
        <Route path="/contratti" element={<Contratti />} />
        <Route path="/contratti/nuovo" element={<ContrattoTemplatePage />} />
        <Route path="/contratti/:id" element={<ContrattoTemplatePage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/impostazioni" element={<Impostazioni />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/onboarding" element={<AdminOnboarding />} />
      <Route path="/*" element={<AuthenticatedApp />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <LanguageProvider>
          <AuthProvider>
            <ThemeProvider>
              <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "")}>
                <AppRoutes />
              </BrowserRouter>
            </ThemeProvider>
          </AuthProvider>
        </LanguageProvider>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
